export interface FamilyMember {
  id: string;
  name: string;
  parentIds?: string[];
  birthdate?: string;
  deathdate?: string;
  imageMetadata?: string;
  spouseId?: string;
  parents?: FamilyMember[]; // New field - contains the parents of this person
  children?: FamilyMember[]; // Deprecated - kept for backward compatibility during migration
  // Progress tracking fields
  editStatus: "incomplete" | "partial" | "complete";
  lastModified?: string; // ISO timestamp
  completionScore?: number; // 0-100 based on filled fields
  isCustomAdded?: boolean; // User added this person (vs template)
  // Photo enhancement tracking
  enhancementStatus?: "none" | "requested" | "enhanced";
  enhancementRequestedAt?: string; // ISO timestamp when enhancement was requested
  // Add other fields (e.g. birthDate, photoUrl) as needed
}

export interface TreeNode extends FamilyMember {
  parents?: TreeNode[]; // New field
  children?: TreeNode[]; // Deprecated - kept for backward compatibility
}

// Progress tracking types
export interface GenerationProgress {
  generation: number; // 0=self, 1=parents, 2=grandparents, etc.
  totalSlots: number;
  filledSlots: number;
  incompleteSlots: number;
  partialSlots: number;
  completeSlots: number;
  completionPercentage: number;
  // Image tracking per generation
  totalImages: number;
  uploadedImages: number;
  missingImages: number;
  imageCompletionPercentage: number;
}

export interface FamilyTreeProgress {
  totalMembers: number;
  incompleteMembers: number;
  partialMembers: number;
  completeMembers: number;
  customMembers: number;

  // Image upload tracking
  totalImages: number;
  uploadedImages: number;
  missingImages: number;

  // Generation-based progress (up to 5 generations back)
  generationProgress: GenerationProgress[];

  // Overall metrics
  overallCompletion: number; // 0-100%
  dataQualityScore: number; // Based on how complete each person is
  lastActivity?: string; // When last edit was made
}

// Required fields for completion (death date is optional)
export const REQUIRED_FIELDS = ["name", "birthdate"] as const;
export const OPTIONAL_FIELDS = ["deathdate", "imageMetadata"] as const;

/**
 * Builds a nested array representing the selected member and up to `generations` levels of ancestors.
 * @param members All family members
 * @param selectedId The member ID to start from
 * @param generations Number of ancestor generations to include
 * @returns Array containing a single root TreeNode with nested parents
 */
export function getAncestors(
  members: FamilyMember[],
  selectedId: string | null,
  generations = 2
): TreeNode[] {
  if (!selectedId) return [];
  const memberMap = new Map<string, FamilyMember>(
    members.map((m) => [m.id, m])
  );

  function buildNode(id: string, depth: number): TreeNode | null {
    const m = memberMap.get(id);
    if (!m) return null;
    const node: TreeNode = { ...m };
    if (depth > 0 && m.parentIds) {
      node.children = m.parentIds
        .map((pid) => buildNode(pid, depth - 1))
        .filter((n): n is TreeNode => n !== null);
    }
    return node;
  }

  const root = buildNode(selectedId, generations);
  return root ? [root] : [];
}

/**
 * Placeholder for future descendant lookup.
 */
export function getDescendants(
  members: FamilyMember[],
  selectedId: string | null,
  generations = 2
): TreeNode[] {
  // TODO: implement descendant traversal
  return [];
}
